import React from 'react';
import './VideoBadgeProgress.css';

/**
 * Gamified lifetime badge progress bar (Duolingo-quest style).
 * Tracks total successfully processed videos (never resets).
 * Progress fills within the current tier; bar resets on tier-up.
 */

export const BADGE_TIERS = [
  { key: 'none', name: 'No badge', start: 0, size: 1 },
  { key: 'bronze', name: 'Bronze', start: 1, size: 3 },      // 1–3
  { key: 'silver', name: 'Silver', start: 4, size: 6 },      // 4–9
  { key: 'gold', name: 'Gold', start: 10, size: 15 },        // 10–24
  { key: 'platinum', name: 'Platinum', start: 25, size: 25 },// 25–49
  { key: 'diamond', name: 'Diamond', start: 50, size: 0 },   // 50+
];

export function getBadgeTierIndex(count) {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  for (let i = BADGE_TIERS.length - 1; i >= 0; i--) {
    if (n >= BADGE_TIERS[i].start) return i;
  }
  return 0;
}

export function getBadgeProgress(count) {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  const tierIndex = getBadgeTierIndex(n);
  const tier = BADGE_TIERS[tierIndex];
  const isMax = tierIndex === BADGE_TIERS.length - 1;
  if (isMax) {
    return { tierIndex, tier, current: tier.start, target: tier.start, percent: 100, isMax, nextTier: null };
  }
  const current = tierIndex === 0 ? n : n - tier.start + 1;
  const target = tier.size;
  return {
    tierIndex,
    tier,
    current,
    target,
    percent: Math.min(100, Math.round((current / target) * 100)),
    isMax,
    nextTier: BADGE_TIERS[tierIndex + 1],
  };
}

export const TIER_LABEL_COLORS = {
  none: '#9aa1ad',
  bronze: '#e07a1a',
  silver: '#8d95a3',
  gold: '#d19700',
  platinum: '#5d8aa5',
  diamond: '#2a93c9',
};

const TIER_BAR_GRADIENTS = {
  none: ['#c9ced6', '#e6e9ed'],
  bronze: ['#b1601c', '#ffb066'],
  silver: ['#8d95a3', '#e9edf1'],
  gold: ['#c98a00', '#ffe27a'],
  platinum: ['#3f7d99', '#bfe6f2'],
  diamond: ['#1f7ba8', '#a4ecff'],
};

export function hexToRgba(hex, alpha) {
  const clean = (hex || '').replace('#', '');
  const bigint = parseInt(clean, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Pro-success-style overlay theme vars for a badge tier. */
export function getTierOverlayThemeStyle(tierKey) {
  const key = tierKey || 'bronze';
  const [start, end] = TIER_BAR_GRADIENTS[key] || TIER_BAR_GRADIENTS.bronze;
  return {
    '--badge-tier-color': TIER_LABEL_COLORS[key] || TIER_LABEL_COLORS.bronze,
    '--badge-tier-glow-a': hexToRgba(start, 0.38),
    '--badge-tier-glow-b': hexToRgba(end, 0.24),
    '--badge-tier-glow-medal': hexToRgba(start, 0.42),
  };
}

const THEME_GRADIENT = ['#5170ff', '#ff66c4'];

/** Tier-colored upward glow for the scrolling notch (no bottom spill). */
export function getTierNotchGlowStyle(tierKey) {
  const key = tierKey || 'none';
  const [start, end] = key === 'none'
    ? THEME_GRADIENT
    : (TIER_BAR_GRADIENTS[key] || TIER_BAR_GRADIENTS.none);
  return {
    '--notch-glow-a': hexToRgba(start, 0.42),
    '--notch-glow-b': hexToRgba(end, 0.32),
    '--notch-glow-edge-a': hexToRgba(start, 0.52),
  };
}

function tierLabelText(tier, tierIndex, isMax) {
  if (tierIndex === 0) return 'Process your first video';
  if (isMax) return 'Diamond';
  return tier.name;
}

const TIER_SHIELD_IMAGES = {
  none: 'noshield.png',
  bronze: 'bronzeshield.png',
  silver: 'silvershield.png',
  gold: 'goldshield.png',
  platinum: 'platinumshield.png',
  diamond: 'diamondshield.png',
};

export function BadgeMedal({ tierKey, locked = false, size = 40, showLockChip = false }) {
  const key = tierKey || 'none';
  const fileName = TIER_SHIELD_IMAGES[key] || TIER_SHIELD_IMAGES.none;
  return (
    <span
      className={`vbp-medal-wrap vbp-medal-glow--${key}${showLockChip ? ' vbp-medal-wrap--upcoming' : ''}`}
      style={{ width: size, height: size }}
    >
      <img
        src={`${process.env.PUBLIC_URL || ''}/${fileName}`}
        alt=""
        width={size}
        height={size}
        className={`vbp-medal ${locked ? 'vbp-medal--locked' : ''}`}
        aria-hidden="true"
        draggable={false}
      />
      {showLockChip && (
        <span className="vbp-lock-chip" aria-hidden="true">
          <svg width="9" height="10" viewBox="0 0 9 10" fill="none">
            <rect x="1" y="4" width="7" height="5.2" rx="1.3" fill="#fff" />
            <path d="M2.3 4V2.7a2.2 2.2 0 0 1 4.4 0V4" stroke="#fff" strokeWidth="1.1" fill="none" />
          </svg>
        </span>
      )}
    </span>
  );
}

export function getBadgeTierSeenStorageKey(uid) {
  return `makereels_badge_tier_seen_${uid || 'anon'}`;
}

export function readStoredBadgeTierIndex(uid) {
  try {
    const raw = localStorage.getItem(getBadgeTierSeenStorageKey(uid));
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  } catch {
    return null;
  }
}

export function writeStoredBadgeTierIndex(uid, tierIndex) {
  try {
    localStorage.setItem(getBadgeTierSeenStorageKey(uid), String(tierIndex));
  } catch {
    /* storage unavailable */
  }
}

export default function VideoBadgeProgress({
  count = 0,
  medalRef = null,
  hideCurrentMedal = false,
  medalHighlight = false,
}) {
  const progress = getBadgeProgress(count);
  const { tier, current, target, percent, isMax, nextTier, tierIndex } = progress;
  const currentLabel = tierLabelText(tier, tierIndex, isMax);
  const currentLabelColor = TIER_LABEL_COLORS[tier.key] || TIER_LABEL_COLORS.none;
  const [gradStart, gradEnd] = TIER_BAR_GRADIENTS[tier.key] || TIER_BAR_GRADIENTS.none;
  const countText = isMax ? '50+' : `${current} / ${target}`;

  return (
    <div className="vbp-wrap">
      <div className="vbp-body">
        <div className={`vbp-bar-row${isMax ? ' vbp-bar-row--max' : ''}`}>
          <div className="vbp-badge-col vbp-badge-current">
            <div
              ref={medalRef}
              className={`vbp-medal-slot${medalHighlight ? ' vbp-medal-slot--highlight' : ''}${hideCurrentMedal ? ' vbp-medal-slot--hidden' : ''}`}
            >
              <BadgeMedal tierKey={tier.key} locked={tierIndex === 0} size={52} />
            </div>
            <div className="vbp-label-micro">Current</div>
            <div
              className={`vbp-label vbp-label--${tier.key}`}
              style={{ color: currentLabelColor }}
            >
              {currentLabel}
            </div>
          </div>
          <div className="vbp-bar">
            <div className="vbp-bar-track">
              <div
                className="vbp-bar-fill"
                style={{
                  width: `${percent}%`,
                  background: `linear-gradient(90deg, ${gradStart} 0%, ${gradEnd} 100%)`,
                }}
              />
              {!isMax && (
                <>
                  {/* Skip 50% — centered count text lives there and would overlap */}
                  <span className={`vbp-checkpoint${percent >= 25 ? ' vbp-checkpoint--done' : ''}`} style={{ left: '25%' }} />
                  <span className={`vbp-checkpoint${percent >= 75 ? ' vbp-checkpoint--done' : ''}`} style={{ left: '75%' }} />
                </>
              )}
              <span className="vbp-bar-text vbp-bar-text--base">{countText}</span>
              <span
                className="vbp-bar-text vbp-bar-text--fill"
                style={{ clipPath: `inset(0 ${100 - percent}% 0 0)` }}
              >
                {countText}
              </span>
            </div>
          </div>
          {!isMax && nextTier && (
            <div className="vbp-badge-col vbp-badge-next" title={`Next: ${nextTier.name}`}>
              <div className="vbp-medal-slot">
                <BadgeMedal tierKey={nextTier.key} locked size={68} showLockChip />
              </div>
              <div className="vbp-label-micro">Next</div>
              <div
                className={`vbp-label vbp-label--${nextTier.key}`}
                style={{ color: TIER_LABEL_COLORS[nextTier.key] || TIER_LABEL_COLORS.none }}
              >
                {nextTier.name}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
